/**
 * `EndOverflowShadow` — the "more columns to the right" affordance for a horizontally
 * scrollable body: one full-height fade at the card's inline-END edge, shown only while
 * content is actually hidden past it. The horizontal twin of `BottomOverflowShadow`, and the
 * unpinned counterpart of `PinnedColumnShadows`' right rail.
 *
 * OPT-IN (`showEndOverflowShadow`), unlike the bottom fade and the pinned rails: those two
 * carry DataGridPro parity (`data-with-bottom-overlay`, the pinned box-shadow), and this one
 * has no predecessor at all — the old grid drew nothing at the scrollport's right edge. A
 * table that does not ask for it keeps exactly the look it shipped with.
 *
 * COLOUR: a SURFACE fade, exactly like `BottomOverflowShadow` — each plane's own paper
 * elevation, opaque at the card's right edge, fading to the same colour at zero alpha. It
 * started as the pinned rail's black gradient (`pinnedShadowRailClass.right`), which was
 * wrong twice over (C-1965): a 16px black rail over an opaque cell only DARKENS the value
 * under it instead of dissolving it, and a narrow dark band at a card edge reads as a "bar"
 * drawn on the table rather than as content passing off the edge. A pinned rail is a shadow
 * because something opaque casts it; nothing sits at the scrollport's end, so the correct
 * affordance there is the same dissolve the last rows get.
 *
 * BANDS: the fade spans the full card height, so it crosses the two STICKY CHROME BANDS (the
 * header at the top, the summary row at the bottom) which paint their own elevation. One flat
 * card-coloured fade would show as a patch of the wrong elevation over each band, so the
 * background is LAYERED per band — `endFadeBackgroundStyle`, the inline-axis sibling of
 * `stickyBandBackgroundStyle`, which already layers the same two bands over the scrollbar
 * gutter. Header and summary labels then fade into THEIR plane, the body cells into the card.
 *
 * PLACEMENT: mount inside the NON-scrolling `position: relative` wrapper that bounds the
 * scroll container (the preset's card), like the other two overlays — so the fade spans the
 * full visible height as ONE element and stays glued to the card edge under scroll. NEVER as
 * a CSS `mask` on the scroll container: a mask hides the scrollbars in Chromium (the bug that
 * dropped the old mask-based fade; see `dataTableRootVariants`). Inset by the scrollbar
 * dimensions so it never paints over the vertical scrollbar (`right`) or the horizontal one
 * (`bottom`). `pointer-events-none` + the low `z-[1]` make it a pure visual overlay that
 * still paints ABOVE the scrolled cells — which is what lets it fade their TEXT, not only the
 * background behind them.
 *
 * A table with RIGHT-PINNED columns renders no end fade: the pinned group already owns that
 * edge and `PinnedColumnShadows` already shadows it, so a second overlay there would fight
 * the shadow on the same boundary. That gate lives in the preset, next to the pin gutters.
 *
 * STATE ON THE CLASS: the fade carries `ui-end-shadow-shown` while shown. It sits ABOVE the
 * scroll container, which opens its own stacking context, so nothing a consumer renders INSIDE
 * the table can paint over it at any z-index. A consumer with its own edge-anchored chrome (the
 * hands grid's row action rail) therefore steps aside while the fade is shown, and this class
 * is the only signal it can read — through `:has()` on a shared ancestor, with no script.
 * `data-end-shadow` (`shown` / `hidden`) is a test hook.
 */
import { cn } from '@gto/ui/cn'
import { END_SHADOW_SHOWN_MARKER } from '@gto/ui/state-markers'

import {
  type DataTableSurface,
  DEFAULT_DATA_TABLE_SURFACE,
  endFadeBackgroundStyle,
  type StickyBand,
} from './data-table.styles'

export const END_SHADOW_WIDTH_PX = 64

export type EndOverflowShadowProps = {
  show: boolean
  surface?: DataTableSurface
  bands?: { header?: StickyBand; summary?: StickyBand }
  /**
   * Vertical-scrollbar width (px) from `useHorizontalOverflowShadows`. The fade sits this far
   * short of the card's right edge so it never paints over the scrollbar. `0` with overlay
   * scrollbars (macOS) or no vertical overflow.
   */
  scrollbarWidth?: number
  /**
   * Horizontal-scrollbar height (px) from `useVerticalOverflowShadow`. The fade stops this far
   * above the card's bottom edge, for the same reason.
   */
  scrollbarHeight?: number
}

export const EndOverflowShadow = ({
  show,
  surface = DEFAULT_DATA_TABLE_SURFACE,
  bands,
  scrollbarWidth = 0,
  scrollbarHeight = 0,
}: EndOverflowShadowProps) => (
  <div
    aria-hidden
    data-end-shadow={show ? 'shown' : 'hidden'}
    className={cn(
      'pointer-events-none absolute top-0 z-[1] transition-opacity duration-200',
      show ? 'opacity-100' : 'opacity-0',
      show && END_SHADOW_SHOWN_MARKER,
    )}
    style={{
      right: scrollbarWidth,
      bottom: scrollbarHeight,
      width: END_SHADOW_WIDTH_PX,
      ...endFadeBackgroundStyle({ body: surface, ...bands }),
    }}
  />
)
